import React, { useEffect, useMemo, useState } from 'react';

import ComboBox, { ComboxBoxProps } from 'ds/ComboBox';
import ComboBoxResult from 'ds/ComboBox/ComboBoxResult';
import NoMatchesItem from 'ds/ComboBox/NoMatchesItem';
import Icon from 'ds/Icon';
import Layout from 'ds/Layout';
import { Props as TextInputProps } from 'ds/inputs/TextInput';
import { AdminListLicensor } from 'ux/Admin/Licensors/types';

export interface Props
  extends
    Pick<TextInputProps, 'size' | 'placeholder' | 'disabled' | 'required' | 'isReadOnly' | 'icon'>,
    ComboxBoxProps {
  label?: string;
  licensors: AdminListLicensor[];
  selectedLicensorId?: number | null;
  errorMessage?: string;
  onSelect: (licensor: AdminListLicensor | undefined) => void;
  clearQueryAfterSelect?: boolean;
  onCreateNew?: (query?: string) => void;
  readOnlyHrefBuilder?: (licensor: AdminListLicensor) => string;
}

const LicensorComboBox: React.FC<Props> = ({
  size,
  licensors,
  label = 'Licensor',
  selectedLicensorId,
  errorMessage,
  onSelect,
  placeholder = 'Search licensors by name or email',
  disabled,
  required = false,
  clearQueryAfterSelect,
  onCreateNew,
  isReadOnly,
  readOnlyHref,
  icon,
  readOnlyHrefBuilder
}) => {
  const selectedLicensor = useMemo(
    () => licensors.find(currentLicensor => currentLicensor.id === selectedLicensorId),
    [licensors, selectedLicensorId]
  );

  const formatQuery = (licensor?: AdminListLicensor) => {
    if (!licensor) return undefined;
    return licensor.name;
  };

  const [query, setQuery] = useState<string | undefined>(formatQuery(selectedLicensor));
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    setQuery(formatQuery(selectedLicensor));
  }, [selectedLicensor]);

  const filteredLicensors = query
    ? licensors.filter(
        licensor =>
          licensor.name.toLowerCase().includes(query.toLowerCase()) ||
          licensor.email?.toLowerCase().includes(query.toLowerCase())
      )
    : licensors;

  const normalizedQuery = (query || '').trim().toLowerCase();
  const hasExactMatch = normalizedQuery.length
    ? licensors.some(currentLicensor => currentLicensor.name.toLowerCase() === normalizedQuery)
    : false;
  const forceShowCreate = !!onCreateNew && normalizedQuery.length > 0 && !hasExactMatch;

  const results = filteredLicensors.map(licensor => ({
    value: licensor.id,
    element: (
      <ComboBoxResult
        key={licensor.id}
        label={licensor.name}
        subLabel={licensor.email || undefined}
        query={query || ''}
      />
    )
  }));

  const effectiveReadOnlyHref =
    readOnlyHref || (readOnlyHrefBuilder && selectedLicensor ? readOnlyHrefBuilder(selectedLicensor) : undefined);

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew(query);
    }
  };

  return (
    <Layout
      onMeasure={({ width }) => {
        setWidth(width);
      }}
      position="relative"
    >
      <ComboBox
        query={query}
        results={results}
        noMatchesItem={
          !!selectedLicensorId ? undefined : <NoMatchesItem size={size} width={width} isCreatable={!!onCreateNew} />
        }
        error={errorMessage}
        size={size}
        label={label}
        bigLabel={placeholder}
        placeholder={placeholder}
        onChange={({ value }) => {
          setQuery(value);
          onSelect(undefined);
        }}
        onCreateNew={onCreateNew ? handleCreateNew : undefined}
        forceShowNoMatchesItem={forceShowCreate}
        onSelect={value => {
          const licensor = licensors.find(currentLicensor => currentLicensor.id === value);

          if (!licensor) {
            setQuery(undefined);
            return;
          }

          onSelect(licensor);
          if (clearQueryAfterSelect) {
            setQuery(undefined);
          } else {
            setQuery(licensor.name);
          }
        }}
        onClear={() => {
          setQuery(undefined);
          onSelect(undefined);
        }}
        highlightMatch
        required={required}
        showResultsWhenEmpty
        hideLabel
        maxItems={8}
        isReadOnly={isReadOnly}
        readOnlyHref={effectiveReadOnlyHref}
        disabled={disabled && !isReadOnly}
      />
      {icon && isReadOnly && effectiveReadOnlyHref && (
        <div
          style={{
            position: 'absolute',
            right: '12px',
            top: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'all',
            zIndex: 1000
          }}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            window.open(effectiveReadOnlyHref, '_blank', 'noopener,noreferrer');
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <Icon name={icon} size="md" color="gray-500" />
        </div>
      )}
    </Layout>
  );
};

export default LicensorComboBox;
